import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAPI from '../services/api.service';
import {
  clearAccessToken,
  onAuthFailure,
  purgeLegacyTokenStores,
  setAccessToken,
} from '../services/token.store';

const AuthContext = createContext(null);

const ANONYMOUS = Object.freeze({
  isLoggedIn: false,
  userInfo: null,
  permissions: null,
  userDept: null,
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const API = useAPI();
  const [authState, setAuthState] = useState(ANONYMOUS);
  // True until the "do I already have a session?" probe below settles. Routing
  // must wait for it: without this the app renders logged-out for one frame and
  // a signed-in user reloading /siop/overview gets bounced to /login.
  const [bootstrapping, setBootstrapping] = useState(true);
  const [contextLoading, setContextLoading] = useState(false);
  const [QCFull, setQCFull] = useState(false);
  const [selectedFilterSection, setSelectedFilterSection] = useState(null);
  const [selectedFilterDepartment, setSelectedFilterDepartment] = useState('');

  // Fetch the signed-in user + their permissions. Permissions are resolved by
  // the backend per request rather than read from the token, so a role change
  // lands on the next call.
  const getCurrentDetails = useCallback(
    async ({ silent = false } = {}) => {
      const response = await API.getCurrentUser({ silent });
      const user = response?.data;
      if (!user) throw new Error('No user in /current-user response');
      setAuthState({
        isLoggedIn: true,
        userInfo: user,
        permissions: user.permissions || [],
        userDept: user.department ?? null,
      });
      return user;
    },
    [API]
  );

  // Session probe, once per mount. The access token lives in sessionStorage and
  // so is absent in a brand-new tab, but the refresh token is an httpOnly cookie
  // that outlives it — the request interceptor turns the resulting 401 into a
  // refresh + retry. So this recovers a session the JS here cannot see, and a
  // genuine failure just means "not signed in".
  useEffect(() => {
    let cancelled = false;
    purgeLegacyTokenStores();
    getCurrentDetails({ silent: true })
      .catch(() => {
        if (!cancelled) setAuthState(ANONYMOUS);
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getCurrentDetails]);

  // Feature-level permission check, against the matrix the backend resolved.
  const checkPermission = useCallback(
    (featureId, permission) => {
      if (!authState.permissions) return false;
      const feature = authState.permissions.find((f) => f.featureId === featureId);
      if (permission === 'read') return feature?.read || false;
      if (permission === 'write') return feature?.write || false;
      return false;
    },
    [authState.permissions]
  );

  const login = useCallback(({ user, permissions, accessToken }) => {
    purgeLegacyTokenStores();
    setAccessToken(accessToken);
    setAuthState({
      isLoggedIn: true,
      userInfo: user ?? null,
      permissions: permissions || [],
      userDept: user?.department ?? null,
    });
  }, []);

  const clearSession = useCallback(() => {
    clearAccessToken();
    purgeLegacyTokenStores();
    setAuthState(ANONYMOUS);
  }, []);

  const logout = useCallback(async () => {
    // Server first: this is what revokes the stored refresh token and clears the
    // httpOnly cookie. Skipping it (as the old header-local handler did) left the
    // session valid server-side no matter what the browser forgot.
    try {
      await API.logout();
    } catch {
      // Already expired, or the network is down. The local teardown below still
      // has to happen — a failed call must never leave the user signed in.
    }
    clearSession();
    navigate('/login', { replace: true });
  }, [API, clearSession, navigate]);

  // Raised by the axios interceptor when a refresh fails mid-session. Tears down
  // the same way as an explicit sign-out, minus the (pointless) logout call.
  useEffect(
    () =>
      onAuthFailure(() => {
        clearSession();
        navigate('/login', { replace: true });
      }),
    [clearSession, navigate]
  );

  return (
    <AuthContext.Provider
      value={{
        authState,
        setAuthState,
        bootstrapping,
        login,
        logout,
        QCFull,
        setQCFull,
        contextLoading,
        setContextLoading,
        checkPermission,
        getCurrentDetails,
        selectedFilterSection,
        setSelectedFilterSection,
        selectedFilterDepartment,
        setSelectedFilterDepartment,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
};
